import { AppDispatch, RootState } from "@Reducers/index";
import axiosWrapper from "@AxiosWrapper";
import { getBackendUrl } from "@Utils";
import { setUserTagLoading, setUserTagData, setUpdatedUserProfileDetails } from "../reducers/userTagData";
import type { GetUserDetailsByIdRequest, UserTagProfileData } from "@ServerTypes/accounts/accounts.schema";
import type { Exact } from "@Helpers/types/exact";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";

/**
 * SCHEMA GAP: the route declares `getUserDetailsByIdResponse`, which is the user ROW, but the
 * handler (`account.helper.ts` getUserDetailsById) answers with `{ status, data: row }` and leaves
 * `data` empty when the lookup misses — so both members stay optional here. Replace this with the
 * generated type once the route's `responseSchema` describes the envelope.
 */
type GetUserDetailsByIdBody = { status?: boolean; data?: UserTagProfileData };

export const fetchUserTagData = (id: ObjectIdString) => async (dispatch: AppDispatch, getState: () => RootState) => {
  const { userTagData } = getState();

  if (userTagData[id]?.isLoading || userTagData[id]?.isLoaded) {
    return;
  }

  dispatch(setUserTagLoading({ id }));

  try {
    // Fetch user details
    const response = await getUserDetailsById({ userId: id });
    const userDetails = response.data.data;

    // If there's a profile picture to fetch, get the image as a blob
    if (userDetails?.profilePic) {
      const imgResponse = await axiosWrapper.get<Blob>(`${getBackendUrl()}/fileSystemServer/getImage/profile/${id}`, {
        responseType: "blob",
      });

      // Use FileReader to convert blob to Base64
      const reader = new FileReader();

      reader.onloadend = function () {
        // `readAsDataURL` resolves to a data-URL string; anything else means the read failed, and
        // `UserTagProfileData.profilePic` is nullable, so that case stores no picture.
        const profilePic = typeof reader.result === "string" ? reader.result : null;

        // Dispatch the data with the Base64 image URL
        dispatch(setUserTagData({ id, data: { ...userDetails, profilePic } }));
      };

      // Read the Blob as Data URL
      reader.readAsDataURL(imgResponse.data);
    } else {
      // If there's no profile picture, just dispatch the data without it
      dispatch(setUserTagData({ id, data: { ...(userDetails ?? {}), profilePic: null } }));
    }
  } catch (error) {
    console.error("Error fetching user tag data:", error);
    Promise.reject(error);
  }
};

export const updateUserProfileDetails = (data: Record<string, unknown>) => (dispatch: AppDispatch) => {
  dispatch(setUpdatedUserProfileDetails(data));
};

export async function getUserDetailsById<T extends Exact<GetUserDetailsByIdRequest, T>>(data: T) {
  return await axiosWrapper.post<GetUserDetailsByIdBody>(`${getBackendUrl()}/accounts/getUserDetailsById`, data);
}
